/**
 * Kanaal voor live "chart gewijzigd"-seintjes (spec chart-datacache 2026-10-06 §4.2). Bewust een
 * neutraal moduul zonder Angular- of human-design-afhankelijkheden (zelfde patroon als
 * `user-data-local-sync`): de platformlaag (`PlatformInstanceListener`, JWT-interceptor) levert de
 * seintjes aan, de HD-datacache luistert.
 *
 * - Elke API-call draagt `X-Egitura-Client: CLIENT_INSTANCE_ID`; de server stuurt die id mee in het
 *   seintje, zodat een browsertab zijn eigen wijzigingen negeert (die staan al in zijn cache).
 * - Zonder listener zijn de meldingen no-ops.
 */

export const CHART_CLIENT_HEADER = 'X-Egitura-Client';

function createClientInstanceId(): string {
  const cryptoApi = typeof crypto !== 'undefined' ? crypto : undefined;
  if (cryptoApi?.randomUUID) {
    return cryptoApi.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/** Eén id per page load (dus per browsertab, ook bij een gedupliceerde tab). */
export const CLIENT_INSTANCE_ID: string = createClientInstanceId();

/** Soorten wijziging die de server meldt. */
export type ChartChangeKind = 'relations' | 'notes' | 'document' | 'deleted' | 'created';

export interface RemoteChartChange {
  readonly chartIds: readonly string[];
  readonly kinds: readonly ChartChangeKind[];
}

/** Ruwe vorm van het `ChartsChanged`-bericht (System.Text.Json, camelCase). */
export interface ChartsChangedMessage {
  readonly chartIds?: readonly string[] | null;
  readonly kinds?: readonly string[] | null;
  readonly originClientId?: string | null;
}

export interface ChartChangeListener {
  onRemoteChange(change: RemoteChartChange): void;
  /** De live verbinding was weg en is terug: seintjes kunnen gemist zijn. */
  onReconnected(): void;
}

const KNOWN_KINDS: readonly ChartChangeKind[] = ['relations', 'notes', 'document', 'deleted', 'created'];

let listener: ChartChangeListener | undefined;

/** Registreert (of wist, met `undefined`) de luisteraar. */
export function setChartChangeListener(value: ChartChangeListener | undefined): void {
  listener = value;
}

/** Aangeroepen door de hub-listener per ontvangen `ChartsChanged`-bericht. */
export function notifyRemoteChartChange(message: ChartsChangedMessage | null | undefined): void {
  if (!message || message.originClientId === CLIENT_INSTANCE_ID) {
    return;
  }
  const chartIds = (message.chartIds ?? []).filter((id): id is string => typeof id === 'string' && id.length > 0);
  const kinds = (message.kinds ?? []).filter((kind): kind is ChartChangeKind =>
    (KNOWN_KINDS as readonly string[]).includes(kind));
  if (chartIds.length === 0 || kinds.length === 0) {
    return;
  }
  listener?.onRemoteChange({ chartIds, kinds });
}

/** Aangeroepen door de hub-listener na een herverbinding. */
export function notifyChartChannelReconnected(): void {
  listener?.onReconnected();
}
